// Own service-worker registration on `workbox-window` — NOT vite-plugin-pwa's
// `virtual:pwa-register` (`injectRegister: null` in `vite.config.ts`). Shows a
// toast when a new SW version is WAITING and reloads ONLY the tab whose
// operator clicked «Обновить». Safe to call at boot before the router mounts.
//
// Pairs with `registerType: 'prompt'` in `vite.config.ts`. Under the former
// `autoUpdate` the new SW activated on its own (`skipWaiting` + `clientsClaim`)
// and the client reloaded the tab immediately — on 2026-09-18 that reload
// landed between a comment-delete's CORS preflight and the DELETE itself, so
// the operator's click was lost without a trace. The reload must be the
// operator's decision, taken between actions, never the SW's.
//
// Why not the plugin's `registerSW`: its prompt-mode client hangs a
// `controlling → location.reload()` listener in EVERY tab that saw the
// waiting SW. `skipWaiting()` changes the controller of every client of the
// registration (no `clients.claim()` needed — that is only for uncontrolled
// clients), so a click in tab A reloaded tab B mid-action: the very class of
// incident the mode switch was meant to end. Here the reload is guarded by a
// per-tab flag that only this tab's click sets; a tab whose controller changed
// underneath it keeps its persistent toast (text switches to «обновлено в
// другой вкладке») and reloads when ITS operator asks — never silently
// staying on the old bundle either: an old bundle under a new SW would 404 on
// lazy chunks missing from the new precache.
import { Workbox } from 'workbox-window';
import { toast } from '@wit-support/ui';

let registered = false;

/** URL + scope the plugin generates the SW under (`filename: 'sw.js'`, `base: '/'`). */
export const SW_URL = '/sw.js';
export const SW_SCOPE = '/';

/**
 * Stable sonner id: every path that (re)announces an update — the `waiting`
 * event, our own `updatefound` watch for a SECOND deploy in a long-lived tab,
 * the «updated in another tab» notice — updates ONE toast instead of stacking
 * a second infinite one the desktop `Toaster` (no close button) cannot dismiss.
 */
export const PWA_UPDATE_TOAST_ID = 'pwa-update';

/**
 * Как часто спрашивать сервер, не появился ли новый SW. Под `prompt` браузер
 * сам проверяет `sw.js` только при навигации и раз в сутки по своему таймеру,
 * а вкладка оператора живёт днями без единой навигации — без опроса тост
 * «Доступна новая версия» она не увидела бы вообще. Час — компромисс между
 * «выкатили — узнали» и лишними запросами.
 */
export const SW_UPDATE_INTERVAL_MS = 60 * 60 * 1000;

/** The slice of `ServiceWorkerRegistration` this module touches. */
export interface SwRegistrationLike {
  update: () => Promise<unknown>;
  readonly installing?: ServiceWorker | null;
  readonly waiting?: ServiceWorker | null;
  addEventListener?: (type: 'updatefound', listener: () => void) => void;
}

/** The slice of `workbox-window`'s `Workbox` this module touches (mockable in tests). */
export interface WorkboxLike {
  addEventListener: (type: 'waiting' | 'controlling', listener: () => void) => void;
  register: () => Promise<SwRegistrationLike | undefined>;
  messageSkipWaiting: () => void;
}

/** The slice of `window` this module touches — injected so tests can observe `reload`. */
export interface PwaWindow {
  setInterval: (handler: () => void, ms: number) => unknown;
  document: Pick<Document, 'visibilityState' | 'addEventListener'>;
  location: { reload: () => void };
  navigator: { serviceWorker?: { controller?: unknown } };
}

/**
 * Периодический `registration.update()` — только для ВИДИМОЙ вкладки: фоновые
 * вкладки браузер и так троттлит, а их обновление никому не нужно, пока к
 * ним не вернулись. Возврат к вкладке (`visibilitychange`) проверяет сразу,
 * чтобы не ждать ещё час после выходных.
 */
export function scheduleSwUpdates(
  registration: { update: () => Promise<unknown> } | undefined,
  win: Pick<PwaWindow, 'setInterval' | 'document'> = window,
): void {
  if (!registration) return;
  const check = () => {
    if (win.document.visibilityState !== 'visible') return;
    void registration.update().catch(() => {
      // Сеть недоступна / сервер отдал 5xx — просто подождём следующего тика.
    });
  };
  win.setInterval(check, SW_UPDATE_INTERVAL_MS);
  win.document.addEventListener('visibilitychange', check);
}

/**
 * Watch the registration for a SECOND update during this tab's life.
 * `workbox-window` drops its own `updatefound` listener after the first
 * external update (`_onUpdateFound`), so a long-lived tab would never hear of
 * the next deploy: the toast would announce release N while N+1 is waiting.
 * Same trigger — the SW reached `installed` and is the registration's
 * `waiting` — so the first deploy fires both this and workbox's `waiting`;
 * the stable toast id folds them into one.
 *
 * NOT on the very first install (fresh browser): Chrome dispatches
 * `updatefound` for it too, after `register()` resolves, and at `installed`
 * the SW is momentarily the registration's `waiting` — but with no
 * controller there is nothing to update, and the toast would offer a
 * pointless reload. An update exists only while a controller does.
 */
function watchFurtherUpdates(
  registration: SwRegistrationLike,
  navigator: PwaWindow['navigator'],
  onWaiting: () => void,
): void {
  registration.addEventListener?.('updatefound', () => {
    const sw = registration.installing;
    if (!sw) return;
    sw.addEventListener('statechange', () => {
      if (sw.state !== 'installed' || registration.waiting !== sw) return;
      if (!navigator.serviceWorker?.controller) return;
      onWaiting();
    });
  });
}

export interface RegisterPwaDeps {
  createWorkbox?: () => WorkboxLike;
  win?: PwaWindow;
}

export function registerPwa(deps: RegisterPwaDeps = {}): void {
  if (registered) return;
  registered = true;
  // `pnpm dev` serves no `/sw.js` (no `devOptions` in vite.config.ts), so a
  // registration would 404 and log an error on every load. The SW is a
  // production concern; dev runs without one.
  if (import.meta.env.DEV) return;

  const win: PwaWindow = deps.win ?? window;
  if (!('serviceWorker' in win.navigator)) return;

  const wb: WorkboxLike = deps.createWorkbox?.() ?? new Workbox(SW_URL, { scope: SW_SCOPE });

  let registration: SwRegistrationLike | undefined;
  // Set ONLY by this tab's «Обновить» click. `controlling` fires in every tab
  // of the registration once any tab's click activated the SW; without the
  // flag it means «another tab did it» and this tab must not reload.
  let requestedHere = false;

  const showPrompt = (description: string) => {
    toast('Доступна новая версия', {
      id: PWA_UPDATE_TOAST_ID,
      description,
      duration: Infinity,
      action: {
        label: 'Обновить',
        onClick: () => {
          requestedHere = true;
          if (registration?.waiting) {
            // Ask the waiting SW to take over; the reload follows on THIS
            // tab's `controlling` event, once the controller has switched.
            wb.messageSkipWaiting();
          } else {
            // Already activated by another tab (or the registration is
            // gone) — nothing to wait for; reload straight away.
            win.location.reload();
          }
        },
      },
    });
  };
  const onWaiting = () => showPrompt('Обновить, когда закончите текущее действие');

  wb.addEventListener('waiting', onWaiting);
  wb.addEventListener('controlling', () => {
    if (requestedHere) {
      win.location.reload();
      return;
    }
    showPrompt(
      'Приложение обновлено в другой вкладке — перезагрузите, когда закончите текущее действие',
    );
  });

  wb.register()
    .then((r) => {
      registration = r;
      if (!r) return;
      watchFurtherUpdates(r, win.navigator, onWaiting);
      scheduleSwUpdates(r, win);
    })
    .catch((err: unknown) => {
      console.error('[pwa] SW registration failed', err);
    });
}
